import { ActionIcon } from "@marketplace/ui";
import { type DataTableColumn } from "@marketplace/ui/data-table";
import { notifications } from "@marketplace/ui/notifications";
import { useQueryClient } from "@tanstack/react-query";
import { getRouteApi } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdOutlineRestorePage, MdOutlineTextSnippet } from "react-icons/md";

import { ENABLE_REGENERATE_INVOICE } from "@/configs";
import { usePostRegenerateInvoiceMutation } from "@/hooks/mutations/use-post-regenerate-invoice-mutation";
import { TransactionStatusPill } from "@/modules/shared";
import { InvoiceService } from "@/services/invoice-service";
import { formatWithOffset } from "@/utils";

import type { TransactionHistoryListItem } from "../dtos/get-transaction-history-list-response-dto";
import { OrderQueryKeys } from "../generators/order-query-keys";
import type { OrderStatus } from "../types";
import { convertOrderStatusToStatusPill } from "../utils/order-status";

const route = getRouteApi("/transaction-history/");

export const useTransactionHistoryListTableColumns = () => {
  const { page, q, sort } = route.useSearch();
  const queryClient = useQueryClient();
  const { t } = useTranslation("order", {
    keyPrefix: "transactionHistoryPage.table.columns"
  });
  const { t: tNotifications } = useTranslation("order", {
    keyPrefix: "transactionHistoryPage.notifications"
  });

  // Track loading states for specific invoices
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>(
    {}
  );

  const { mutate: regenerateInvoice } = usePostRegenerateInvoiceMutation({
    options: {
      onSuccess: (data, variables) => {
        // Clear loading state for this specific invoice
        const loadingKey = `${variables.order_id}-${variables.handling || "regular"}`;
        setLoadingStates((prev) => ({ ...prev, [loadingKey]: false }));

        notifications.show({
          message: tNotifications("regenerateInvoiceSuccess")
        });

        // Auto-download the regenerated invoice if URL is provided
        if (data.data.media_path) {
          InvoiceService.downloadInvoice(data.data.media_path);
        }

        // Invalidate and refetch the current page query
        queryClient.invalidateQueries({
          queryKey: OrderQueryKeys.getTransactionHistoryList({
            page,
            limit: 10,
            q,
            sort
          }).queryKey
        });
      },
      onError: (_, variables) => {
        // Clear loading state for this specific invoice on error
        const loadingKey = `${variables.order_id}-${variables.handling || "regular"}`;
        setLoadingStates((prev) => ({ ...prev, [loadingKey]: false }));
      }
    }
  });

  const handleDownloadInvoice = useCallback((invoiceUrl: string) => {
    InvoiceService.downloadInvoice(invoiceUrl);
  }, []);

  const handleRegenerateInvoice = useCallback(
    (orderId: string, isInternal?: boolean) => {
      // Set loading state for this specific invoice
      const loadingKey = `${orderId}-${isInternal ? "internal" : "regular"}`;
      setLoadingStates((prev) => ({ ...prev, [loadingKey]: true }));

      regenerateInvoice({
        order_id: orderId,
        ...(isInternal && { handling: "internal" })
      });
    },
    [regenerateInvoice, setLoadingStates]
  );

  const handleDownloadClick = useCallback(
    (invoiceUrl: string | null, isInternal: boolean) => {
      if (invoiceUrl) {
        handleDownloadInvoice(invoiceUrl);
      } else {
        notifications.show({
          message: isInternal
            ? tNotifications("internalInvoiceNotAvailable")
            : tNotifications("invoiceNotAvailable"),
          variant: "error"
        });
      }
    },
    [handleDownloadInvoice, tNotifications]
  );

  const columns = useMemo<DataTableColumn<TransactionHistoryListItem>[]>(
    () => [
      {
        accessor: "no",
        title: t("no"),
        render: (_: TransactionHistoryListItem, index: number) =>
          (page - 1) * 10 + index + 1
      },
      {
        accessor: "order_id",
        title: t("orderId"),
        render: (record) => record.id
      },
      {
        accessor: "order_date",
        title: t("orderDate"),
        render: (record) =>
          formatWithOffset(record.order_created_at, "dd/MM/yyyy")
      },
      {
        accessor: "buyer_name",
        title: t("buyerName"),
        render: (record) => record.customer_name ?? "-"
      },
      {
        accessor: "product_name",
        title: t("productName"),
        render: (record) => {
          const orderItems = record.order_items;
          if (!orderItems || orderItems.length === 0) return "-";

          const firstItem = orderItems[0];
          const productName = firstItem?.product_name;

          if (!productName) return "-";

          return orderItems.length > 1 ? `${productName}, ...` : productName;
        }
      },
      {
        accessor: "package",
        title: t("package"),
        render: (record) => {
          const orderItems = record.order_items;
          if (!orderItems || orderItems.length === 0) return "-";

          const firstItem = orderItems[0];
          const packageName = firstItem?.product_package_name;

          if (!packageName) return "-";

          return orderItems.length > 1 ? `${packageName}, ...` : packageName;
        }
      },
      {
        accessor: "status",
        title: t("status"),
        render: (record) => {
          const paymentStatus = convertOrderStatusToStatusPill(
            record.order_status as OrderStatus
          );

          return <TransactionStatusPill status={paymentStatus} />;
        }
      },
      {
        accessor: "receipt",
        title: t("receipt"),
        render: (record) => (
          <div className="flex gap-3 items-center">
            <ActionIcon
              variant="transparent"
              onClick={() => handleDownloadClick(record.invoice_path, false)}
              title="Download Receipt"
            >
              <MdOutlineTextSnippet size={20} />
            </ActionIcon>
            {ENABLE_REGENERATE_INVOICE && (
              <ActionIcon
                variant="transparent"
                onClick={() => handleRegenerateInvoice(record.id, false)}
                loading={loadingStates[`${record.id}-regular`] || false}
                title="Generate Receipt"
              >
                <MdOutlineRestorePage size={20} />
              </ActionIcon>
            )}
          </div>
        )
      },
      {
        accessor: "internal_receipt",
        title: t("internalReceipt"),
        render: (record) => (
          <div className="flex gap-3 items-center">
            <ActionIcon
              variant="transparent"
              onClick={() =>
                handleDownloadClick(record.invoice_path_internal, true)
              }
              title="Download Internal Receipt"
            >
              <MdOutlineTextSnippet size={20} />
            </ActionIcon>
            {ENABLE_REGENERATE_INVOICE && (
              <ActionIcon
                variant="transparent"
                onClick={() => handleRegenerateInvoice(record.id, true)}
                loading={loadingStates[`${record.id}-internal`] || false}
                title="Generate Internal Receipt"
              >
                <MdOutlineRestorePage size={20} />
              </ActionIcon>
            )}
          </div>
        )
      }
    ],
    [page, t, handleDownloadClick, handleRegenerateInvoice, loadingStates]
  );

  return { columns };
};
